export function BitField<
  F extends {
    [key: string]: number;
  },
>(flags: F) {
  abstract class BitField {
    /**
     * BitField of the packed bits
     */
    public bitfield: number;

    /**
     * Flags of the BitField
     */
    public static FLAGS: F = flags;

    /**
     * All flags of the BitField combined
     */
    // eslint-disable-next-line unicorn/consistent-function-scoping
    public static ALL: number = Object.values(flags).reduce((acc, flagValue) => acc | flagValue, 0);

    /**
     * @param bitfield - BitField of the packed bits
     */
    public constructor(bitfield: number) {
      this.bitfield = bitfield;
    }

    /**
     * Checks if the bitfield has a flag
     *
     * @param flag - Flag to check
     */
    public has(flag: number): boolean {
      // AND operation to check if the flag is present
      return (this.bitfield & flag) === flag;
    }

    /**
     * Adds a flag to the bitfield
     *
     * @param flag - Flag to add
     */
    public add(flag: number): this {
      // OR operation to add the flag
      this.bitfield |= flag;
      return this;
    }

    /**
     * Removes a flag from the bitfield
     *
     * @param flag - Flag to remove
     */
    public remove(flag: number): this {
      // AND operation with the negation of the flag to remove it
      this.bitfield &= ~flag;
      return this;
    }

    /**
     * Serializes the bitfield, returning the packed bits
     */
    public serialize(): number {
      return this.bitfield;
    }

    /**
     * Returns an object with the flags of the bitfield
     */
    public serializeFlags(): Record<keyof F, boolean> {
      const serialized: Partial<Record<keyof F, boolean>> = {};

      for (const [flag, value] of Object.entries(BitField.FLAGS) as [keyof F, number][]) {
        serialized[flag] = this.has(value);
      }

      return serialized as Record<keyof F, boolean>;
    }

    /**
     * Creates a BitField from flags
     *
     * @param flags - Flags to create the BitField from
     * @returns The created BitField
     */
    public static fromFlags(flags: Partial<Record<keyof F, boolean>>): BitField {
      let bitfield = 0;

      for (const [flag, value] of Object.entries(flags) as [keyof F, boolean][]) {
        if (value && BitField.FLAGS[flag as keyof typeof BitField.FLAGS]) {
          bitfield |= BitField.FLAGS[flag as keyof typeof BitField.FLAGS]!;
        }
      }

      return new (this as any)(bitfield);
    }
  }

  return BitField;
}
